import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Clock } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import PLButton from '../ui/PLButton'
import PLWordmark from '../ui/PLWordmark'

/**
 * AuthExpiredPL v1.1.4 — Pantalla "Enlace expirado".
 *
 * Supabase redirige a /?#error=access_denied&error_code=otp_expired cuando
 * el OTP del email de confirmación ha caducado. App.js detecta el hash y
 * redirige aquí.
 *
 * La pantalla pre-rellena el email desde localStorage (PL_ONBOARDING_DRAFT)
 * si está disponible, para que el usuario no tenga que escribirlo de nuevo.
 */

function getDraftEmail() {
  try {
    const draft = localStorage.getItem('PL_ONBOARDING_DRAFT')
    if (draft) {
      const parsed = JSON.parse(draft)
      return parsed.email || ''
    }
  } catch {}
  return ''
}

export default function AuthExpiredPL() {
  const navigate = useNavigate()
  const [email, setEmail] = useState(getDraftEmail)
  const [focused, setFocused] = useState(false)
  const [loading, setLoading] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState(null)

  const handleResend = async (e) => {
    e.preventDefault()
    if (!email.trim()) return
    setError(null)
    setLoading(true)
    try {
      const { error: resendErr } = await supabase.auth.resend({
        type: 'signup',
        email: email.trim(),
      })
      if (resendErr) throw resendErr
      setSent(true)
    } catch (err) {
      setError(err.message || 'Error al reenviar. Inténtalo de nuevo.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      data-theme="dark"
      style={{
        minHeight: '100vh',
        background: '#0A0A0A',
        color: 'var(--pl-cream)',
        fontFamily: 'var(--font-sans)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        colorScheme: 'dark',
      }}
    >
      <div style={{ width: '100%', maxWidth: 400, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
        >
          <img src="/logo-mark.svg" alt="" width={48} height={48} aria-hidden />
          <div style={{ marginTop: 14 }}>
            <PLWordmark size="md" style={{ color: 'var(--pl-cream)' }} />
          </div>

          <div
            style={{
              marginTop: 32,
              width: 64,
              height: 64,
              borderRadius: 20,
              background: 'color-mix(in srgb, var(--pl-terracota) 10%, #1A1A1A)',
              border: '1.5px solid color-mix(in srgb, var(--pl-terracota) 25%, transparent)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Clock size={28} color="var(--pl-terracota)" strokeWidth={1.5} />
          </div>

          <h1
            style={{
              margin: '20px 0 0 0',
              fontSize: 22,
              fontWeight: 500,
              color: 'var(--pl-cream)',
              letterSpacing: '-0.01em',
              textAlign: 'center',
              lineHeight: 1.2,
            }}
          >
            Enlace expirado
          </h1>

          <p
            style={{
              margin: '12px 0 0 0',
              maxWidth: 320,
              fontSize: 14,
              lineHeight: 1.6,
              color: '#B5B0A5',
              textAlign: 'center',
            }}
          >
            El enlace de confirmación ha caducado.
            Te enviamos uno nuevo.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.15 }}
          style={{ width: '100%', marginTop: 32 }}
        >
          {sent ? (
            <div
              style={{
                padding: '16px 20px',
                background: 'color-mix(in srgb, var(--pl-terracota) 8%, #1A1A1A)',
                border: '1px solid color-mix(in srgb, var(--pl-terracota) 25%, transparent)',
                borderRadius: 12,
                fontSize: 14,
                lineHeight: 1.55,
                color: '#B5B0A5',
                textAlign: 'center',
              }}
            >
              Enviado. Revisa tu bandeja de entrada.
            </div>
          ) : (
            <form onSubmit={handleResend} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <input
                type="email"
                autoComplete="email"
                placeholder="Tu email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                required
                style={{
                  width: '100%',
                  height: 56,
                  padding: '0 16px',
                  background: '#1A1A1A',
                  border: `1px solid ${focused ? 'var(--pl-terracota)' : '#333'}`,
                  borderRadius: 12,
                  color: 'var(--pl-cream)',
                  fontFamily: 'var(--font-sans)',
                  fontSize: 16,
                  outline: 'none',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.15s',
                  boxShadow: focused ? '0 0 0 2px color-mix(in srgb, var(--pl-terracota) 30%, transparent)' : 'none',
                }}
              />

              {error && (
                <div style={{ color: 'var(--pl-danger)', fontSize: 13, textAlign: 'center' }}>
                  {error}
                </div>
              )}

              <PLButton
                fullWidth
                size="lg"
                type="submit"
                loading={loading}
                style={{
                  background: 'var(--pl-terracota)',
                  color: 'var(--pl-cream)',
                  height: 56,
                  borderRadius: 12,
                }}
              >
                Reenviar enlace
              </PLButton>
            </form>
          )}

          <button
            type="button"
            onClick={() => navigate('/')}
            style={{
              display: 'block',
              width: '100%',
              marginTop: 16,
              background: 'transparent',
              border: 'none',
              color: '#666',
              fontSize: 13,
              cursor: 'pointer',
              fontFamily: 'var(--font-sans)',
              textAlign: 'center',
              padding: '8px 0',
            }}
          >
            Volver al inicio
          </button>
        </motion.div>
      </div>
    </div>
  )
}
